import { TenantType } from '@/api/generatedApi';
import { useGetAssetStateCounts } from './api/assets/useGetAssetStateCounts';
import {
  calculateDaysRemaining,
  calculateTotalAssets,
  calculateUploadPercentage,
  getDisplayText,
  getOverallWarningLevel,
  isTrialExpired,
  type TrialWarningLevel,
} from '@/utils/trial';
import { useTenants } from '@/services/tenants/useTenants';
import { usePreserveDataDuringLoading } from '@/components/FilterData/hooks/usePreserveDataDuringLoading';

export const useTrialStatus = (): {
  isTrial: boolean;
  isExpired?: boolean;
  daysRemaining?: number;
  uploadsRemaining?: number;
  uploadPercentage?: number;
  warningLevel?: TrialWarningLevel;
  displayText?: string;
  isLoading: boolean;
} => {
  const { tenant } = useTenants();
  const { data, isLoading: isCountsLoading } = useGetAssetStateCounts({});
  const assetCounts = usePreserveDataDuringLoading(data, isCountsLoading);

  const isLoading = isCountsLoading || !tenant;
  const isTrial = tenant?.tenantType === TenantType.Trial;

  if (isTrial) {
    const { trialExpiresAt, assetLimit } = tenant;
    const totalAssets = calculateTotalAssets(assetCounts);
    const daysRemaining = trialExpiresAt ? calculateDaysRemaining(trialExpiresAt) : 0;
    const uploadsRemaining = assetLimit ? assetLimit - totalAssets : undefined;
    const uploadPercentage = calculateUploadPercentage(totalAssets, assetLimit);

    return {
      isTrial: true,
      isExpired: isTrialExpired(trialExpiresAt),
      daysRemaining,
      uploadsRemaining,
      uploadPercentage,
      warningLevel: getOverallWarningLevel(daysRemaining, uploadPercentage),
      displayText: getDisplayText(daysRemaining, uploadsRemaining),
      isLoading: false,
    };
  } else {
    return { isTrial: false, isLoading };
  }
};
